import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { routeId as computeRouteId } from "@boon/x402-route";
import { Footer } from "../components/Footer";
import { Nav } from "../components/Nav";
import { X402RecognizeEndpoint } from "../components/x402/X402RecognizeEndpoint";
import { X402ReviewComposer } from "../components/x402/X402ReviewComposer";
import { X402RouteRow } from "../components/x402/X402RouteRow";
import { X402SocialTrail } from "../components/x402/X402SocialTrail";
import {
  fetchX402Route,
  fetchX402PublicUsedRoutes,
  fetchX402Routes,
  fetchX402ServiceEndpoints,
  scanX402Endpoint,
  shouldUseMergedRecentReviewFeed,
  type X402Route,
  type X402PublicUsedRoute,
  type X402ServiceEndpoint,
} from "../lib/x402Discovery";
import { X402PublishDetails } from "../components/x402/X402PublishDetails";
import { X402WalletEndpoints } from "../components/x402/X402WalletEndpoints";
import { X402DiscoveredResults } from "../components/x402/X402DiscoveredResults";
import { RecentX402Reviews } from "../components/x402/RecentX402Reviews";
import { X402EvidenceFrontDoor } from "../components/x402/X402EvidenceFrontDoor";

const REVIEWABLE_METHODS = ["POST", "GET", "PUT", "PATCH", "DELETE"] as const;

function usdcAddress(): `0x${string}` | null {
  const raw = (import.meta.env.VITE_USDC_CONTRACT as string | undefined)?.trim();
  if (!raw || !/^0x[0-9a-fA-F]{40}$/.test(raw) || /^0x0{40}$/.test(raw)) return null;
  return raw as `0x${string}`;
}

export const AGENT_PROMPT = `Load the Boon agent skill: https://docs.boonprotocol.com/skill.md

Check for the Boon CLI with boon --version. If it is missing, ask the user to install @velinussage/boon-cli. Do not install it yourself.

Use AgentCash through Boon for one-off x402 tools:
1. Discover the tool schema:       npx agentcash@latest discover <origin>
2. Pay, call, and publish use:     boon x402 fetch <route> -m POST -b '<json>' --max-amount <usdc> --json
3. Assess the actual result. Payment alone is not quality evidence.
4. Draft a free use review:       boon x402 review use <activity-id> --profile <profile-id> --text "<review>" --json
5. Show the exact route, settlement, signer, text, evidence label, and zero charge. Publish only after approval by adding --confirm-subjective --yes.

A routed Boon is a separate optional recognition act with USDC and a fixed $BOON burn. Follow the skill's proposal flow and ask for exact approval before signing or sending one.

Browse recognized endpoints as JSON: GET https://api.boonprotocol.com/api/v1/x402/graph (a $0.01 x402 read that must not run without cost approval).`;

const STARTER_TASKS = [
  { label: "Research a topic", query: "search", tone: "bg-amber-soft text-amber-deep border-amber/40", Icon: SearchGlyph },
  { label: "Check property data", query: "property", tone: "bg-clay-soft text-clay-deep border-clay/35", Icon: MapGlyph },
  { label: "Find web data", query: "firecrawl", tone: "bg-olive-soft text-olive-deep border-olive/35", Icon: GlobeGlyph },
] as const;

/**
 * Accept whatever an operator has on the clipboard: a plain term, a full x402
 * endpoint URL, or an `npx agentcash …` command that wraps one. Reduce it to
 * the host + path the route listings actually index, so pasted commands and
 * links match instead of returning zero results.
 */
export function normalizeEndpointQuery(raw: string): string {
  const input = raw.trim();
  if (!input) return "";
  const urlMatch = input.match(/https?:\/\/[^\s"'<>]+/i);
  if (urlMatch) {
    try {
      const url = new URL(urlMatch[0]);
      const path = url.pathname.replace(/\/+$/, "");
      return `${url.host}${path}`;
    } catch {
      // fall through to token scan
    }
  }
  if (/^npx\s/i.test(input)) {
    // `npx agentcash@latest discover stableenrich.dev` -> first host-shaped token.
    const hostToken = input
      .split(/\s+/)
      .slice(1)
      .find((token) => !token.startsWith("-") && !/^agentcash(@|$)/i.test(token) && /[a-z0-9-]+\.[a-z]{2,}/i.test(token));
    if (hostToken) return hostToken.replace(/^https?:\/\//i, "").replace(/\/+$/, "");
  }
  return input;
}

export function X402DiscoveryPage() {
  const [params, setParams] = useSearchParams();
  // The evidence-led lane is the free default. The previous recognition
  // directory remains available only through the explicit legacy opt-in.
  const legacyMode = params.get("legacy") === "1" || params.has("sort");
  // Normalize here too, not only on submit: a shared/bookmarked URL (or a
  // pre-deploy tab) can carry a raw pasted command in ?q=.
  const query = normalizeEndpointQuery(params.get("q") ?? "");
  const sort = params.get("sort") === "recent" ? "recent" : "reviewed";
  const [draft, setDraft] = useState(query);
  const [routes, setRoutes] = useState<X402Route[]>([]);
  const [usedRoutes, setUsedRoutes] = useState<X402PublicUsedRoute[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contract, setContract] = useState<string | null>(null);
  const [reviewMethod, setReviewMethod] = useState<(typeof REVIEWABLE_METHODS)[number]>("POST");
  const [reviewHint, setReviewHint] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [endpointPayTo, setEndpointPayTo] = useState<string | null>(null);
  const [endpointIsX402, setEndpointIsX402] = useState<boolean | null>(null);
  const [serviceEndpoints, setServiceEndpoints] = useState<X402ServiceEndpoint[] | null>(null);
  const [serviceLoading, setServiceLoading] = useState(false);
  const [endpointFilter, setEndpointFilter] = useState("");
  const [promptCopied, setPromptCopied] = useState(false);
  const reviewSectionRef = useRef<HTMLDivElement>(null);
  const reviewRequested = params.get("review") === "1";
  const showMergedRecentReviews = shouldUseMergedRecentReviewFeed(query, sort);

  const load = useCallback(async (cursor?: string) => {
    if (!legacyMode) {
      setLoading(false);
      return;
    }
    const more = Boolean(cursor);
    more ? setLoadingMore(true) : setLoading(true);
    setError(null);
    try {
      const result = await fetchX402Routes({ q: query, context: "with", sort, limit: 20, cursor });
      setRoutes((current) => more ? dedupeRoutes([...current, ...result.routes]) : result.routes);
      setContract(result.contract);
      setNextCursor(result.pagination.nextCursor);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
      if (!more) setRoutes([]);
    } finally {
      if (more) setLoadingMore(false);
      else setLoading(false);
    }
  }, [legacyMode, query, sort]);

  useEffect(() => {
    if (!legacyMode) {
      setLoading(false);
      setRoutes([]);
      setError(null);
      return;
    }
    setDraft(query);
    void load();
  }, [legacyMode, load, query]);

  useEffect(() => {
    if (!legacyMode) return;
    let cancelled = false;
    fetchX402PublicUsedRoutes(20)
      .then((items) => { if (!cancelled) setUsedRoutes(items); })
      .catch(() => { if (!cancelled) setUsedRoutes([]); });
    return () => { cancelled = true; };
  }, [legacyMode]);

  const matchingUsedRoutes = useMemo(() => {
    if (!query) return usedRoutes.slice(0, 8);
    const needle = query.toLowerCase();
    return usedRoutes.filter((route) =>
      `${route.method} ${route.origin}${route.path}`.toLowerCase().includes(needle),
    ).slice(0, 8);
  }, [query, usedRoutes]);

  function changeSort(nextSort: "reviewed" | "recent") {
    const next = new URLSearchParams(params);
    if (nextSort === "reviewed") next.delete("sort");
    else next.set("sort", nextSort);
    setParams(next);
  }

  // When a zero-match query is endpoint-shaped, offer the recognize flow.
  const reviewCandidate = useMemo(() => {
    if (!query || !/[a-z0-9-]+\.[a-z]{2,}/i.test(query)) return null;
    const url = /^https?:\/\//i.test(query) ? query : `https://${query}`;
    try {
      return { url, routeId: computeRouteId({ publicEndpointUrl: url, method: reviewMethod }) };
    } catch {
      return null;
    }
  }, [query, reviewMethod]);

  // A bare host (no path) is not an endpoint — the paid routes live at paths.
  // Fetch the host's /.well-known/x402 index so the user can pick one.
  const bareHost = useMemo(() => {
    const stripped = query.replace(/^https?:\/\//i, "");
    return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(stripped) ? stripped : null;
  }, [query]);
  useEffect(() => {
    if (!legacyMode || !bareHost) { setServiceEndpoints(null); return; }
    let cancelled = false;
    setServiceLoading(true);
    setServiceEndpoints(null);
    setEndpointFilter("");
    fetchX402ServiceEndpoints(bareHost)
      .then((eps) => { if (!cancelled) setServiceEndpoints(eps); })
      .catch(() => { if (!cancelled) setServiceEndpoints([]); })
      .finally(() => { if (!cancelled) setServiceLoading(false); });
    return () => { cancelled = true; };
  }, [bareHost, legacyMode]);

  function selectServiceEndpoint(ep: X402ServiceEndpoint) {
    const next = new URLSearchParams(params);
    next.set("q", `${bareHost}${ep.path}`);
    next.set("review", "1");
    setParams(next);
    setReviewMethod((["POST", "GET", "PUT", "PATCH", "DELETE"] as const).includes(ep.method as never) ? ep.method as (typeof REVIEWABLE_METHODS)[number] : "POST");
  }

  // Probe the endpoint for its x402 challenge so the method is detected, not
  // asked. The picker only appears if the scan is inconclusive or the user
  // opts to override. Runs once per endpoint URL.
  const scanUrl = reviewCandidate?.url ?? null;
  useEffect(() => {
    if (!legacyMode) return;
    if (!scanUrl) return;
    let cancelled = false;
    setScanning(true);
    setEndpointPayTo(null);
    setEndpointIsX402(null);
    scanX402Endpoint(scanUrl)
      .then((scan) => {
        if (cancelled) return;
        setEndpointPayTo(scan.payTo ?? null);
        setEndpointIsX402(scan.results.some((r) => r.x402));
        if (scan.recommendedMethod) {
          setReviewMethod(scan.recommendedMethod as (typeof REVIEWABLE_METHODS)[number]);
        }
      })
      .catch(() => { if (!cancelled) setEndpointIsX402(null); })
      .finally(() => {
        if (!cancelled) setScanning(false);
      });
    return () => {
      cancelled = true;
    };
  }, [legacyMode, scanUrl]);

  // After a "Review an endpoint" request resolves (or a search returns nothing),
  // bring the review panel into view so the user doesn't have to scroll.
  useEffect(() => {
    if (!legacyMode) return;
    if ((reviewRequested || (!loading && routes.length === 0 && reviewCandidate)) && reviewSectionRef.current) {
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      reviewSectionRef.current.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" });
    }
  }, [legacyMode, reviewRequested, loading, routes.length, reviewCandidate]);

  // A "Review an endpoint" request against an already-listed route points at
  // the route page, where the composer sits beside its recognition context.
  const listedCandidate = useMemo(() => {
    if (!reviewCandidate) return null;
    return routes.find((route) => route.routeId.toLowerCase() === reviewCandidate.routeId.toLowerCase()) ?? null;
  }, [routes, reviewCandidate]);

  // A routed Boon can recognize a route ID without anyone publishing its
  // description, leaving a "hash-only" record that text search can't surface.
  // Look the exact route ID up directly so a recognized-but-undescribed
  // endpoint shows the review + publish flow instead of asking for another burn.
  const [directRoute, setDirectRoute] = useState<X402Route | null>(null);
  useEffect(() => {
    if (!legacyMode) return;
    const targetRouteId = reviewCandidate?.routeId ?? null;
    if (!targetRouteId || listedCandidate) {
      setDirectRoute(null);
      return;
    }
    let cancelled = false;
    setDirectRoute(null);
    fetchX402Route(targetRouteId)
      .then((res) => {
        if (cancelled) return;
        setDirectRoute(res.route.recognition.routedBoonCount > 0 ? res.route : null);
      })
      .catch(() => { if (!cancelled) setDirectRoute(null); });
    return () => { cancelled = true; };
  }, [legacyMode, reviewCandidate, listedCandidate]);

  // Either a text-search hit or a direct hash-only lookup means the route is
  // recognized and reviewable.
  const recognizedRoute = listedCandidate ?? directRoute;
  const usdc = usdcAddress();
  const needsDescription = !!directRoute && directRoute.description === null && !!usdc;

  function submit(event: FormEvent) {
    event.preventDefault();
    const next = new URLSearchParams(params);
    const normalized = normalizeEndpointQuery(draft);
    if (normalized) next.set("q", normalized);
    else next.delete("q");
    next.delete("review");
    setParams(next);
    setReviewHint(null);
  }

  function chooseStarterTask(term: string) {
    setDraft(term);
    const next = new URLSearchParams(params);
    next.set("q", term);
    next.delete("review");
    setParams(next);
    setReviewHint(null);
  }

  function requestReview() {
    const normalized = normalizeEndpointQuery(draft);
    if (!normalized || !/[a-z0-9-]+\.[a-z]{2,}/i.test(normalized)) {
      setReviewHint("Enter an endpoint URL (or paste an npx agentcash command) to review it.");
      return;
    }
    const next = new URLSearchParams(params);
    next.set("q", normalized);
    next.set("review", "1");
    setParams(next);
    setReviewHint(null);
  }

  if (!legacyMode) {
    return (
      <>
        <Nav current="x402" />
        <X402EvidenceFrontDoor initialQuery={query} />
        <Footer />
      </>
    );
  }

  return (
    <>
      <Nav current="x402" />
      <main className="overflow-x-clip">
        <section className="px-6 pt-8 md:px-10 md:pt-12 max-w-[1440px] mx-auto">
          <header className="grid gap-7 animate-fade-up lg:grid-cols-[minmax(0,0.82fr)_minmax(30rem,1.18fr)] lg:items-start">
            <div className="min-w-0">
              <p className="text-sm text-muted btn-mono tracking-wide uppercase">legacy recognition directory</p>
              <h1 className="mt-2 max-w-2xl text-4xl font-display tracking-tight leading-[1.02] md:text-5xl">Most recognized x402 routes</h1>
              <p className="mt-4 max-w-2xl text-base text-ink-soft leading-relaxed md:text-lg">
                This older route directory is an explicit opt-in view. For a free task-first search with evidence labels and no-spend handoff, return to <Link className="text-olive-deep hover:underline" to="/x402">free service discovery</Link>.
              </p>
            </div>

            <aside className="min-w-0 overflow-hidden rounded-xl border border-faint bg-paper-deep/35 p-5 md:p-6" aria-labelledby="starter-task-heading">
              <div className="min-w-0 pl-2">
                <h2 id="starter-task-heading" className="text-xl font-display tracking-tight">What should your agent do?</h2>
                <p className="mt-0.5 text-sm text-muted">Start with a task. The protocol details can wait.</p>
              </div>
              <div className="mt-4 flex flex-wrap gap-2" aria-label="Starter tasks">
                {STARTER_TASKS.map(({ label, query: taskQuery, tone, Icon }) => (
                  <button key={label} type="button" className={`inline-flex min-h-10 max-w-full items-center gap-2 rounded-full border px-3 text-sm font-semibold whitespace-nowrap transition-[border-color,transform] hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-olive ${tone}`} onClick={() => chooseStarterTask(taskQuery)}>
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{label}</span>
                  </button>
                ))}
              </div>
              <form onSubmit={submit} className="mt-4">
                <div className="grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                  <label className="block min-w-0">
                    <span className="sr-only">Describe what you want your agent to do</span>
                    <input className="w-full min-w-0 min-h-12 rounded-lg border border-faint bg-paper px-4 py-3 text-base focus:border-olive focus:outline-none focus:ring-2 focus:ring-olive/25" value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="Research a company, check a parcel…" />
                  </label>
                  <button className="btn btn-primary min-h-12 w-full whitespace-nowrap sm:w-auto" type="submit">Search</button>
                </div>
              </form>
            </aside>
          </header>
        </section>

        <section className="px-6 mt-9 md:px-10 md:mt-12 max-w-[1440px] mx-auto" aria-labelledby="social-trail-heading">
          <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 id="social-trail-heading" className="text-2xl font-display tracking-tight md:text-3xl">Start with a public identity</h2>
              <p className="mt-2 max-w-2xl text-sm text-muted leading-relaxed">The public trail is still small. Every card is tied to a linked social or agent identity and a labeled review or supported use record.</p>
            </div>
            <Link className="btn-mono text-xs text-olive-deep hover:underline whitespace-nowrap" to="/board">Browse Boon profiles →</Link>
          </div>
          {loading ? <SocialTrailLoading /> : <X402SocialTrail routes={routes} />}
        </section>

        <section className="px-6 mt-12 md:px-10 md:mt-16 max-w-[1440px] mx-auto">
          <div>
            {matchingUsedRoutes.length > 0 && (
              <section className="mb-12" aria-labelledby="public-x402-use-heading">
                <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                  <div className="max-w-3xl">
                    <h2 id="public-x402-use-heading" className="text-2xl font-display tracking-tight">What linked wallets tried recently</h2>
                    <p className="mt-2 text-sm text-muted leading-relaxed">These are public route-plus-settlement signals from linked wallets. They show activity, not an endorsement or quality score.</p>
                  </div>
                  <details className="text-sm text-muted">
                    <summary className="cursor-pointer min-h-11 inline-flex items-center btn-mono text-xs text-olive-deep whitespace-nowrap">How use is verified</summary>
                    <p className="max-w-xl pb-2 leading-relaxed">Provider receipts bind use to a route. Otherwise a wallet names the route and Boon verifies its Base settlement.</p>
                  </details>
                </div>
                <ol className="grid gap-3 md:grid-cols-2">
                  {matchingUsedRoutes.map((route) => <UsedRouteCard key={route.routeId} route={route} />)}
                </ol>
              </section>
            )}
            {loading && <Panel text="Finding endpoint reviews and public use…" />}
            {!loading && error && <Panel text="Boon could not load endpoint reviews and public use." detail={error} />}
            {/* When the empty search resolves to a reviewable endpoint, skip the
                "no matches" panel and go straight to the review section below. */}
            {!loading && !error && routes.length === 0 && !(reviewCandidate && contract) && (
              <Panel compact text={query ? `No endpoint reviews match “${query}” yet.` : "No endpoint reviews yet."} detail="Matching x402 endpoints appear below. A catalog listing does not imply use, quality, or provider success." />
            )}
            {!loading && !error && routes.length > 0 && (
              <>
                <div className="mb-5 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                  <div className="max-w-3xl">
                    <h2 className="text-2xl font-display tracking-tight">{query ? `Starting points for “${query}”` : "Tools with endpoint reviews"}</h2>
                    <p className="mt-2 text-sm text-muted leading-relaxed">{showMergedRecentReviews ? "Newest shows the merged public trail, including reviews attached to settlement-backed use. Most recognized shows routes with ranked Boon-backed conviction first." : "See who left the review, what they said, and the task the tool handles. Open a route only when you want the technical detail."}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Order tools">
                    <button type="button" className={`inline-flex min-h-10 items-center gap-2 rounded-full border px-3 btn-mono text-xs whitespace-nowrap ${sort === "reviewed" ? "border-olive bg-olive-soft text-olive-deep" : "border-faint bg-paper text-muted"}`} aria-pressed={sort === "reviewed"} onClick={() => changeSort("reviewed")}>
                      <SignalGlyph className="h-3.5 w-3.5" /> Most recognized
                    </button>
                    <button type="button" className={`inline-flex min-h-10 items-center gap-2 rounded-full border px-3 btn-mono text-xs whitespace-nowrap ${sort === "recent" ? "border-olive bg-olive-soft text-olive-deep" : "border-faint bg-paper text-muted"}`} aria-pressed={sort === "recent"} onClick={() => changeSort("recent")}>
                      <ClockGlyph className="h-3.5 w-3.5" /> Newest
                    </button>
                  </div>
                </div>
                {showMergedRecentReviews ? (
                  <RecentX402Reviews
                    className=""
                    limit={10}
                    showExploreLink={false}
                    showHeader={false}
                  />
                ) : (
                  <div className="grid min-w-0 gap-4 xl:grid-cols-2">
                    {routes.map((route) => <X402RouteRow key={route.routeId} route={route} />)}
                  </div>
                )}
                <details className="mt-4 text-xs text-muted leading-relaxed">
                  <summary className="cursor-pointer min-h-11 inline-flex items-center btn-mono text-xs text-olive-deep whitespace-nowrap">How ordering and evidence work</summary>
                  <p className="max-w-3xl pb-2">{sort === "reviewed" ? "Ranked recognition appears first, with recency and public recognition breaking ties." : "Newest merges evidence-labeled reviews by publication time. Reviews attached to settlement-backed use remain unranked."} Review text is subjective. A Boon is a public conviction signal, not a universal quality score.</p>
                </details>
                {nextCursor && !showMergedRecentReviews && (
                  <button className="btn btn-ghost mt-5" type="button" disabled={loadingMore} onClick={() => void load(nextCursor)}>
                    {loadingMore ? "Loading…" : "Load earlier routes"}
                  </button>
                )}
              </>
            )}
            {!loading && !error && reviewCandidate && contract && (routes.length === 0 || reviewRequested) && (
              <div ref={reviewSectionRef} className="scroll-mt-6">{
              recognizedRoute && contract ? (
                <section className="mt-8 border border-faint bg-paper rounded-md p-5 md:p-6">
                  <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Recognized endpoint</p>
                  <h2 className="mt-2 text-xl font-display">Add a written review</h2>
                  <p className="mt-2 text-sm text-muted leading-relaxed max-w-3xl">
                    {reviewCandidate.url} is recognized, so you can publish a wallet-signed review. For the full recognition history and receipt-verified lanes, <Link to={`/x402/routes/${recognizedRoute.routeId}`} className="text-olive-deep hover:underline">open its route page</Link>.
                  </p>
                  {needsDescription && (
                    <div className="mt-4 border border-olive/40 bg-olive-soft/20 rounded-md p-4">
                      <p className="text-sm text-ink font-display">This endpoint has no public description yet</p>
                      <p className="mt-1 text-sm text-muted leading-relaxed">
                        It is recognized on-chain but only by its route ID, so it does not surface when people search its URL. If you recognized it, publish a description to make it findable.
                      </p>
                      {usdc && (
                        <X402PublishDetails
                          endpointUrl={reviewCandidate.url}
                          method={reviewMethod}
                          usdc={usdc}
                          onPublished={() => void load()}
                        />
                      )}
                    </div>
                  )}
                  <X402ReviewComposer
                    key={`review-${recognizedRoute.routeId}`}
                    contract={contract}
                    routeId={recognizedRoute.routeId}
                    routeUrl={reviewCandidate.url}
                    endpointMethod={reviewMethod}
                    receiptPayTo=""
                    receiptSigner={null}
                    receiptStatus="not_observed"
                    recognitions={null}
                    reviews={[]}
                    onPublished={() => void load()}
                  />
                </section>
              ) : (
                <section className="mt-8 border border-faint bg-paper rounded-md p-5 md:p-6">
                  <p className="btn-mono text-[11px] uppercase tracking-[0.14em] text-muted">Be the first signal</p>
                  <h2 className="mt-2 text-xl font-display">Recognize this endpoint</h2>
                  <p className="mt-2 text-sm text-muted leading-relaxed max-w-3xl">
                    An endpoint becomes discoverable only after someone sends it a routed Boon. A plain review cannot create a listing. If you have used {reviewCandidate.url} and it delivered, recognize it below to seed it in the graph.
                  </p>
                  {/* A bare host is not an endpoint — pick a specific paid route
                      from its published x402 index. */}
                  {bareHost && (
                    <div className="mt-4">
                      {serviceLoading ? (
                        <p className="text-xs text-muted">Reading {bareHost}'s published endpoints…</p>
                      ) : serviceEndpoints && serviceEndpoints.length > 0 ? (
                        <div className="max-w-2xl">
                          <p className="text-sm text-muted">Pick which paid endpoint on {bareHost} to recognize:</p>
                          <input
                            className="mt-2 w-full border border-faint rounded-md bg-paper px-3 py-2 text-sm text-ink"
                            aria-label={`Filter ${serviceEndpoints.length} published endpoints`}
                            placeholder={`Filter ${serviceEndpoints.length} endpoints…`}
                            value={endpointFilter}
                            onChange={(e) => setEndpointFilter(e.target.value)}
                          />
                          <div className="mt-2 max-h-72 overflow-y-auto border border-faint rounded-md divide-y divide-faint">
                            {serviceEndpoints
                              .filter((ep) => {
                                const q = endpointFilter.trim().toLowerCase();
                                return !q || ep.path.toLowerCase().includes(q) || (ep.description ?? "").toLowerCase().includes(q);
                              })
                              .slice(0, 100)
                              .map((ep) => (
                                <button
                                  key={`${ep.method} ${ep.path}`}
                                  type="button"
                                  onClick={() => selectServiceEndpoint(ep)}
                                  className="w-full text-left px-3 py-2 hover:bg-paper-deep/40"
                                >
                                  <span className="flex items-center gap-2">
                                    <span className="btn-mono text-[10px] rounded border border-faint px-1.5 py-0.5 text-muted">{ep.method}</span>
                                    <span className="btn-mono text-xs text-ink break-all">{ep.path}</span>
                                    {ep.priceUsd && <span className="btn-mono text-xs text-olive-deep ml-auto shrink-0">${ep.priceUsd}</span>}
                                  </span>
                                  {ep.description && <span className="block mt-0.5 text-xs text-muted line-clamp-2">{ep.description}</span>}
                                </button>
                              ))}
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-muted">Enter the full endpoint URL including its path, for example <code className="break-all">https://{bareHost}/api/...</code>, not just the host.</p>
                      )}
                    </div>
                  )}
                  {/* The burn UI shows only once a specific endpoint path is chosen. */}
                  {!bareHost && (
                    <>
                      {scanning && <p className="mt-3 text-xs text-muted">Reading the endpoint's x402 challenge…</p>}
                      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-muted">
                        <span className="btn-mono text-[10px] uppercase tracking-[0.14em]">Method</span>
                        {REVIEWABLE_METHODS.map((method) => (
                          <button
                            key={method}
                            type="button"
                            className={`btn-mono rounded-md border px-2 py-1 ${reviewMethod === method ? "border-olive bg-olive-soft text-olive-deep" : "border-faint text-muted hover:border-ink-soft"}`}
                            aria-pressed={reviewMethod === method}
                            onClick={() => setReviewMethod(method)}
                          >
                            {method}
                          </button>
                        ))}
                        <span>{scanning ? "detecting…" : endpointIsX402 ? "confirm the method this endpoint is called with (it sets the route ID)" : ""}</span>
                      </div>
                      <X402RecognizeEndpoint
                        key={reviewCandidate.routeId}
                        routeId={reviewCandidate.routeId}
                        endpointUrl={reviewCandidate.url}
                        method={reviewMethod}
                        endpointPayTo={endpointPayTo}
                        isX402={endpointIsX402}
                        contract={contract}
                        onRecognized={() => void load()}
                      />
                    </>
                  )}
                </section>
              )
            }</div>
            )}
            {!loading && !error && (
              <X402DiscoveredResults query={query} onPick={(url) => {
                const normalized = normalizeEndpointQuery(url);
                setDraft(normalized);
                const next = new URLSearchParams(params);
                next.set("q", normalized);
                next.set("review", "1");
                setParams(next);
                setReviewHint(null);
              }} />
            )}

            <details className="mt-12 border-y border-faint py-4">
              <summary className="cursor-pointer min-h-11 inline-flex items-center gap-2 btn-mono text-xs text-olive-deep whitespace-nowrap"><WrenchGlyph className="h-4 w-4" /> Builder tools</summary>
              <div className="pb-3 pt-4">
                <p className="max-w-3xl text-sm text-muted leading-relaxed">Paste an endpoint URL, inspect a wallet’s paid providers, review a route you used, or copy the coding-agent prompt.</p>
                <X402BuilderEndpointForm
                  value={draft}
                  onChange={setDraft}
                  onReview={requestReview}
                  hint={reviewHint}
                />
                <X402WalletEndpoints onPick={(url) => {
                  const normalized = normalizeEndpointQuery(url);
                  setDraft(normalized);
                  const next = new URLSearchParams(params);
                  next.set("q", normalized);
                  next.set("review", "1");
                  setParams(next);
                  setReviewHint(null);
                }} />
                <div className="mt-5 overflow-hidden rounded-lg border border-faint bg-paper">
                  <div className="flex flex-col gap-3 border-b border-faint px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-sm text-muted leading-relaxed">Give Claude Code, Cursor, or another agent the ready-to-run Boon workflow.</p>
                    <button type="button" onClick={() => { void navigator.clipboard.writeText(AGENT_PROMPT); setPromptCopied(true); window.setTimeout(() => setPromptCopied(false), 1500); }} className="min-h-11 w-full shrink-0 rounded-md border border-faint px-3 py-2 btn-mono text-xs text-olive-deep hover:border-ink-soft whitespace-nowrap sm:w-auto">{promptCopied ? "Copied" : "Copy agent prompt"}</button>
                  </div>
                  <pre className="max-w-full overflow-x-auto p-4 text-xs text-ink whitespace-pre-wrap break-words font-mono bg-paper-deep/40">{AGENT_PROMPT}</pre>
                </div>
              </div>
            </details>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

export function X402BuilderEndpointForm({
  value,
  onChange,
  onReview,
  hint,
}: {
  value: string;
  onChange: (value: string) => void;
  onReview: () => void;
  hint: string | null;
}) {
  return (
    <form
      className="mt-4 max-w-3xl rounded-xl border border-faint bg-paper-deep/35 p-4 md:p-5"
      onSubmit={(event) => {
        event.preventDefault();
        onReview();
      }}
    >
      <label className="block" htmlFor="builder-endpoint-input">
        <span className="text-sm font-semibold text-ink">Endpoint URL or AgentCash command</span>
        <span className="mt-1 block text-xs leading-relaxed text-muted">
          Recognized routes open the full review form. New routes open the recognition step first.
        </span>
      </label>
      <div className="mt-3 grid min-w-0 gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
        <input
          id="builder-endpoint-input"
          className="min-h-12 w-full min-w-0 rounded-lg border border-faint bg-paper px-4 py-3 text-sm text-ink focus:border-olive focus:outline-none focus:ring-2 focus:ring-olive/25"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="https://service.example/api/tool or npx agentcash…"
          spellCheck={false}
          aria-describedby={hint ? "builder-endpoint-error" : undefined}
        />
        <button className="btn btn-primary min-h-12 w-full whitespace-nowrap sm:w-auto" type="submit">
          Review endpoint
        </button>
      </div>
      {hint && <p id="builder-endpoint-error" className="mt-2 text-sm text-red-700" role="alert">{hint}</p>}
    </form>
  );
}

function SocialTrailLoading() {
  return (
    <div className="rounded-xl border border-faint bg-paper p-5" role="status">
      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-paper-deep text-muted" aria-hidden="true"><PersonGlyph className="h-6 w-6" /></span>
        <div><p className="text-sm font-semibold text-ink">Finding public identities…</p><p className="mt-1 text-xs text-muted">Reading recent social and wallet activity.</p></div>
      </div>
    </div>
  );
}

function UsedRouteCard({ route }: { route: X402PublicUsedRoute }) {
  const receiptBacked = route.receiptUseCount > 0;
  return (
    <li className="rounded-xl border border-faint bg-paper p-4 transition-[border-color,transform] duration-150 hover:-translate-y-0.5 hover:border-olive focus-within:border-olive">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-soft text-amber-deep" aria-hidden="true"><CursorGlyph className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink break-words">{friendlyUsedRouteLabel(route)}</p>
          <p className="mt-1 btn-mono text-xs text-muted break-all"><span className="text-olive-deep">{route.method}</span> {route.origin}{route.path}</p>
        </div>
        <span className="pill pill-faint btn-mono text-[10px] shrink-0">{receiptBacked ? "receipt-backed" : "wallet-attested"}</span>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-faint pt-3">
        <p className="text-xs text-muted">
          {route.useCount} public {route.useCount === 1 ? "use" : "uses"} · {route.walletCount} {route.walletCount === 1 ? "wallet" : "wallets"}
          {route.reviewCount > 0 ? ` · ${route.reviewCount} signed ${route.reviewCount === 1 ? "opinion" : "opinions"}` : ""}
        </p>
        <Link className="btn-mono text-xs text-olive-deep hover:underline whitespace-nowrap" to={`/x402/routes/${route.routeId}`}>See route →</Link>
      </div>
    </li>
  );
}

function friendlyUsedRouteLabel(route: X402PublicUsedRoute): string {
  const path = route.path.toLowerCase();
  if (/exa|search/.test(path)) return "Search the web for focused research";
  if (/property|parcel/.test(path)) return "Check recent property changes";
  try {
    return `Use ${new URL(route.origin).hostname.replace(/^www\./, "")}`;
  } catch {
    return "Use this x402 tool";
  }
}

function Panel({ text, detail, compact = false }: { text: string; detail?: string; compact?: boolean }) {
  return (
    <div className={`text-center ${compact ? "py-5" : "py-12"}`}>
      <p className={`font-display ${compact ? "text-lg md:text-xl" : "text-2xl"}`}>{text}</p>
      {detail && <p className={`${compact ? "mt-1 text-xs" : "mt-2 text-sm"} text-muted`}>{detail}</p>}
    </div>
  );
}

function dedupeRoutes(routes: X402Route[]): X402Route[] {
  return [...new Map(routes.map((route) => [route.routeId, route])).values()];
}

type GlyphProps = { className?: string };
function SearchGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="10.5" cy="10.5" r="5.5"/><path d="m15 15 5 5"/></svg>; }
function MapGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="m4 6 5-2 6 2 5-2v14l-5 2-6-2-5 2zM9 4v14M15 6v14"/></svg>; }
function GlobeGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18"/></svg>; }
function CursorGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="m5 3 12 9-6 1.5L8.5 19z"/><path d="m12 14 4 5"/></svg>; }
function SignalGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 20v-4M12 20v-8.5M18 20V7"/></svg>; }
function ClockGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>; }
function WrenchGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M14.5 6.5a4 4 0 0 0-5 5L4 17v3h3l5.5-5.5a4 4 0 0 0 5-5l-3 3-3-3z"/></svg>; }
function PersonGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="8" r="3.25"/><path d="M5.5 20c.7-4 3-6 6.5-6s5.8 2 6.5 6"/></svg>; }
